/**
 * Canonical check: is this record a CATALOGUE voucher (`is_voucher_deal`
 * truthy), as opposed to a regular bookable deal?
 *
 * Why a shared helper (Dennis 2026-06-15, q=172 "deal-voucher €64,50" RCA):
 * the `is_voucher_deal` flag arrives with INCONSISTENT types depending on the
 * BFF endpoint that produced the record:
 *
 *   - `/deals/package-as-voucher` (voucher listing) returns a real boolean `true`
 *   - `/deals/package|booking/<cat>/<id>` (deal-by-id) returns the STRING `"false"`
 *     for a regular deal (and would be the string `"true"` for a voucher)
 *
 * A naive `!deal.is_voucher_deal` treats the string `"false"` as TRUTHY. That
 * is the exact trap already called out in `services/deals.js`. Every surface
 * that branches on "is this a voucher?" must go through this helper so they
 * all agree regardless of which endpoint fed them the record.
 *
 * Returns `true` only for the affirmative forms the BFF emits in practice
 * (`true`, `1`, `"true"`, `"1"`). Anything else (`"false"`, `false`, `0`,
 * `null`, `undefined`, missing) is treated as a regular deal.
 *
 * @param {{ is_voucher_deal?: unknown }} deal
 * @returns {boolean}
 */
export function isVoucherDeal(deal) {
  const v = deal?.is_voucher_deal;
  return v === true || v === 1 || v === "true" || v === "1";
}

/**
 * Should this request render/charge as a DEAL-voucher (a genuine bookable deal
 * sold as a one-off gift voucher, priced at the deal's "vanaf" x 2), given the
 * raw `?kind=` query param and the ALREADY-RESOLVED deal?
 *
 * Server-side trust boundary (2026-07-07): the voucher checkout + detail routes
 * must NOT honour `kind=deal-voucher` on the query string alone. A crafted URL
 * that puts `kind=deal-voucher` on a CATALOGUE voucher row (`is_voucher_deal`
 * truthy — the live `/deals/package-as-voucher` listing returns a real boolean
 * `true` for these) would otherwise flip the page to deal-voucher pricing (the
 * deal's "vanaf" x 2) instead of the catalogue price the operator set. Only
 * honour the kind for a genuine bookable deal (`is_voucher_deal` falsy — checked
 * via `isVoucherDeal` so the string "false" the deal-by-id BFF emits is handled);
 * otherwise fall back to catalogue pricing.
 *
 * Why this is display/UX correctness and not a money leak: the backend recomputes
 * the deal-voucher charge from the `dealId` authoritatively
 * (`computeDealVoucherValueCents`, vanaf x 2) and discards the client `amount`, so
 * a crafted URL can never set an attacker-chosen price. But the backend gates the
 * deal-voucher path only by "deal row exists + is priced" (table membership in
 * `cms_deals`), NOT by `is_voucher_deal` — so this frontend guard is the only
 * place that enforces "a catalogue voucher keeps its catalogue price". Gating here
 * also stops the checkout from sending `kind`/`dealId` to create-intent at all for
 * a catalogue voucher, and keeps the voucher detail page and the checkout page
 * making the identical decision so they never drift (the class of drift behind the
 * PR #1027 "€ 0,-" checkout bug).
 *
 * @param {unknown} kind  the raw `searchParams.kind` value
 * @param {{ is_voucher_deal?: unknown }} deal  the resolved deal record
 * @returns {boolean}
 */
export function isDealVoucherActive(kind, deal) {
  return kind === "deal-voucher" && !isVoucherDeal(deal);
}
